import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAssessment } from '~/api/assessments'
import type { AdminAssessmentDetail } from '~/api/types'
import { STATUS_TONES } from '~/components/AssessmentTable'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { SidePanelDetail } from '~/components/ui/side-panel-detail'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { formatDateTime } from '~/lib/utils'

// 인증평가 상세 슬라이드오버. 기본정보 + 점수표 + 보고서 요약(있으면).
const VERDICT_TONES: Record<string, BadgeTone> = {
    pass: 'success',
    fail: 'danger',
    partial: 'warning',
    pending: 'neutral',
}

export function AssessmentDetailPanel({
    assessmentId,
    onClose,
}: {
    assessmentId: string
    onClose: () => void
}) {
    const navigate = useNavigate()
    const [detail, setDetail] = useState<AdminAssessmentDetail | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        getAssessment(assessmentId)
            .then((data) => {
                if (alive) setDetail(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [assessmentId])

    return (
        <SidePanelDetail
            title='평가 상세'
            data={detail}
            loading={loading}
            error={error}
            onClose={onClose}
            headerActions={
                <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                        onClose()
                        navigate(`/assessments/${assessmentId}`)
                    }}>
                    상세보기
                </Button>
            }>
            {(detail) => (
                <>
                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            기본정보
                        </h3>
                        <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
                            <Field label='제목' value={detail.title} />
                            <Field
                                label='상태'
                                value={
                                    <Badge
                                        tone={
                                            STATUS_TONES[detail.status] ??
                                            'neutral'
                                        }>
                                        {detail.status}
                                    </Badge>
                                }
                            />
                            <Field label='규모' value={detail.scale ?? '-'} />
                            <Field
                                label='업종'
                                value={detail.industry ?? '-'}
                            />
                            <Field
                                label='분류'
                                value={detail.classification ?? '-'}
                            />
                            <Field
                                label='단계'
                                value={detail.assessmentType ?? '-'}
                            />
                            <Field
                                label='점수'
                                value={`${detail.totalScore ?? '-'}${
                                    detail.maxScore != null
                                        ? ` / ${detail.maxScore}`
                                        : ''
                                }`}
                            />
                            <Field
                                label='합격 가능성'
                                value={detail.passLikelihood ?? '-'}
                            />
                            <Field
                                label='답변 수'
                                value={detail.answersCount}
                            />
                            <Field
                                label='생성일'
                                value={formatDateTime(detail.createdAt)}
                            />
                        </dl>
                    </section>

                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            점수표
                        </h3>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>항목</TableHead>
                                    <TableHead>점수</TableHead>
                                    <TableHead>판정</TableHead>
                                    <TableHead>방법</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {detail.scores.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            className='text-muted-foreground py-6 text-center'>
                                            점수가 없어요.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    detail.scores.map((s) => (
                                        <TableRow key={s.itemId}>
                                            <TableCell className='font-mono text-xs'>
                                                {s.itemId}
                                            </TableCell>
                                            <TableCell className='whitespace-nowrap'>
                                                {s.score ?? '-'}
                                                {s.maxScore != null
                                                    ? ` / ${s.maxScore}`
                                                    : ''}
                                            </TableCell>
                                            <TableCell>
                                                {s.verdict ? (
                                                    <Badge
                                                        tone={
                                                            VERDICT_TONES[
                                                                s.verdict
                                                            ] ?? 'neutral'
                                                        }>
                                                        {s.verdict}
                                                    </Badge>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {s.method ?? '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </section>

                    {detail.reportSummary ? (
                        <section>
                            <h3 className='text-foreground mb-2 text-sm font-bold'>
                                보고서 요약
                            </h3>
                            <p className='text-foreground whitespace-pre-wrap'>
                                {detail.reportSummary}
                            </p>
                        </section>
                    ) : null}
                </>
            )}
        </SidePanelDetail>
    )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div>
            <dt className='krds-label'>{label}</dt>
            <dd className='text-foreground mt-0.5'>{value}</dd>
        </div>
    )
}
